import type { ReactNode } from 'react';
import styled from 'styled-components';
import { emitToolDismiss, emitToolUsage } from './emitter';
import { useFormContext } from 'react-hook-form';
import type { ToolName } from '@ben/columbia/components/assistant/types';
import { useThrottledCallback } from '@mantine/hooks';

export interface AIToolWrapperProps {
  toolName: ToolName;
  fieldName: string;
  children: ReactNode;
  additionalValue?: unknown;
}

const getFocusTargetValue = (target: EventTarget | null) => {
  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  ) {
    return target.value;
  }

  return undefined;
};

const AIToolWrapper = ({
  children,
  toolName,
  fieldName,
  additionalValue,
}: AIToolWrapperProps) => {
  const { setValue, getValues } = useFormContext();

  const handleInteraction = (value: unknown) => {
    const currentValue = value ?? getValues(fieldName);

    emitToolUsage({
      toolName,
      componentKey: fieldName,
      currentValue,
      additionalValue,
      onChangeValue: (newValue) => {
        setValue(fieldName, newValue, { shouldValidate: true });
        handleInteraction(newValue);
      },
    });
  };

  // dismiss the tool after 2 seconds, this is to prevent the tool
  // from being dismissed when the user is still interacting with it
  const dismissInteraction = useThrottledCallback(() => {
    emitToolDismiss(toolName, fieldName);
  }, 2000);

  return (
    <FocusContainer
      onInput={(event) => handleInteraction(getFocusTargetValue(event.target))}
      onFocus={(event) => handleInteraction(getFocusTargetValue(event.target))}
      onBlur={dismissInteraction}
    >
      {children}
    </FocusContainer>
  );
};

export default AIToolWrapper;

const FocusContainer = styled.div`
  all: unset;
  display: contents;
`;
